import { SKIP, visit } from 'unist-util-visit';

import {
  FOOTNOTE_NUMBER_HAST_PROP,
  FOOTNOTE_PATTERN,
} from '../MarkdownRenderer.constants';

import type { Element, ElementContent, Root, Text } from 'hast';

/**
 * Rehype plugin that converts [^N] footnote patterns in text nodes
 * directly into <sup data-footnote-number="N"> elements.
 *
 * Handles text nodes containing multiple patterns, e.g.:
 * "text [^1] more [^2] end" → [text, <sup>, text, <sup>, text]
 *
 * The CustomSup component reads the data attribute to render FootnoteCitation.
 */
export const rehypeFootnoteCitations = (): ((tree: Root) => void) => {
  return (tree: Root) => {
    visit(tree, 'text', (node: Text, index, parent) => {
      if (index === undefined || !parent) return;

      const text = node.value;
      if (!text.includes('[^')) return;

      // Create a fresh regex to avoid shared lastIndex state
      const pattern = new RegExp(FOOTNOTE_PATTERN.source, 'g');
      const newNodes: ElementContent[] = [];
      let lastIndex = 0;
      let match: RegExpExecArray | null;

      while ((match = pattern.exec(text)) !== null) {
        if (match.index > lastIndex) {
          newNodes.push({
            type: 'text',
            value: text.slice(lastIndex, match.index),
          });
        }

        const num = match[1];
        newNodes.push({
          type: 'element',
          tagName: 'sup',
          properties: { [FOOTNOTE_NUMBER_HAST_PROP]: num },
          children: [{ type: 'text', value: num }],
        });

        lastIndex = pattern.lastIndex;
      }

      if (newNodes.length === 0) return;

      if (lastIndex < text.length) {
        newNodes.push({ type: 'text', value: text.slice(lastIndex) });
      }

      (parent as Element).children.splice(index, 1, ...newNodes);

      return [SKIP, index + newNodes.length];
    });
  };
};
